// EmailCollectionModal.tsx - Collects email for social logins without email (simplified)
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
} from '@mui/material';
import { Email as EmailIcon } from '@mui/icons-material';

interface EmailCollectionModalProps {
  open: boolean;
  onEmailProvided: (email: string) => void;
  onCancel: () => void;
  providerName?: string;
}

const EmailCollectionModal: React.FC<EmailCollectionModalProps> = ({
  open,
  onEmailProvided,
  onCancel,
  providerName = 'social login',
}) => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = () => {
    // Basic email validation
    if (!email || !email.includes('@') || !email.includes('.')) {
      setError('Please enter a valid email address');
      return;
    }

    // Email provided - create account immediately!
    onEmailProvided(email);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <EmailIcon />
          <Typography variant="h6">
            Email Required
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body1" gutterBottom>
          <strong>{providerName}</strong> didn't share your email with us.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Please provide your email to complete your account setup. We'll use it for notifications and account recovery.
        </Typography>

        <TextField
          fullWidth
          label="Email Address"
          type="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setError(''); // Clear error on change
          }}
          onKeyDown={handleKeyDown}
          placeholder="your@email.com"
          autoFocus
          error={!!error}
          helperText={error}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!email}
        >
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmailCollectionModal;
